// ============================================================
// drum - the Armoury drum's arithmetic, pure.
//
// The rack is a CYLINDER (v8.192.0): the visible plates form a ring of
// real rack positions, the cursor sits in the middle, and everything is
// placed by its circular distance from it. These are the numbers behind
// that - offsets, wrapping steps, the wheel's notches, the stacking and
// the keyboard's meaning - with no React and no DOM, so each can be
// tested on its own. `useDrum` wires them to the tab.
// ============================================================

/**
 * THE DRUM IS A CYLINDER - CIRCULAR distance, not linear. The plate
 * "after" the last one is the first, so with the cursor on chamber 0
 * the final weapon sits one step ABOVE it, not n-1 below. Returns the
 * signed offset of rack position `i` from `centre` in (-n/2, n/2]:
 * negative is above the centre. 0 for anything not on the ring.
 */
export function circularOffset(ring: number[], centre: number, i: number): number {
  const n = ring.length;
  if (n === 0) return 0;
  const pi = ring.indexOf(i);
  const pc = ring.indexOf(centre);
  if (pi < 0 || pc < 0) return 0;
  const raw = (((pi - pc) % n) + n) % n;
  return raw > n / 2 ? raw - n : raw;
}

/**
 * The rack position `step` chambers round the ring from `from`,
 * wrapping at both ends. A `from` that is not on the ring (a filter hid
 * it) counts as the first chamber.
 */
export function ringStep(ring: number[], from: number, step: number): number {
  const p = ring.indexOf(from);
  return ring[((p < 0 ? 0 : p) + step + ring.length) % ring.length];
}

/** Pixels per wheel delta unit, by `WheelEvent.deltaMode`: pixel, line (Firefox), page. */
const WHEEL_MODE_PX = [1, 33, 120];

/** A wheel event's vertical travel in pixels. */
export function wheelPixels(deltaY: number, deltaMode: number): number {
  return deltaY * (WHEEL_MODE_PX[deltaMode] ?? 1);
}

/** The wheel's running total, and when it last moved (ms). */
export interface WheelAcc {
  total: number;
  last: number;
}

/** A pause longer than this starts a fresh gesture (ms). */
const WHEEL_IDLE_MS = 250;
/** Travel that makes one notch (px). */
const WHEEL_NOTCH_PX = 60;

/**
 * Feed one wheel event into the accumulator. Trackpads deliver many
 * small deltas, so they add up to a notch; a pause resets the total.
 * Returns the new accumulator and the step it produced (0 for none).
 */
export function wheelNotch(prev: WheelAcc, deltaPx: number, now: number): { acc: WheelAcc; step: -1 | 0 | 1 } {
  const total = (now - prev.last > WHEEL_IDLE_MS ? 0 : prev.total) + deltaPx;
  if (Math.abs(total) < WHEEL_NOTCH_PX) return { acc: { total, last: now }, step: 0 };
  return { acc: { total: 0, last: now }, step: total > 0 ? 1 : -1 };
}

/** Space between stacked plates (px). */
const STACK_GAP = 4;

/**
 * Each row's top (px) in the viewport, by ring position: the centre row
 * (`centrePos`) at the viewport's middle, the rest stacked outward by
 * their measured `heights` in circular-offset order, `STACK_GAP` apart.
 */
export function stackPositions(
  offsets: number[],
  heights: number[],
  centrePos: number,
  viewportHeight: number,
): number[] {
  const y: number[] = new Array(offsets.length).fill(0);
  y[centrePos] = viewportHeight / 2 - heights[centrePos] / 2;
  const byOffset = offsets.map((o, k) => ({ o, k })).sort((a, b) => a.o - b.o);
  const ci = byOffset.findIndex((e) => e.k === centrePos);
  for (let k = ci + 1; k < byOffset.length; k++) {
    const prev = byOffset[k - 1].k;
    y[byOffset[k].k] = y[prev] + heights[prev] + STACK_GAP;
  }
  for (let k = ci - 1; k >= 0; k--) {
    const cur = byOffset[k].k;
    y[cur] = y[byOffset[k + 1].k] - STACK_GAP - heights[cur];
  }
  return y;
}

/** What a key pressed on a plate means: reorder the weapon, or move the cursor to a rack position. */
export type PlateKeyIntent = { kind: 'reorder'; delta: 1 | -1 } | { kind: 'goto'; to: number } | null;

const ARROW_STEP: Record<string, 1 | -1> = { ArrowDown: 1, ArrowUp: -1 };

/**
 * The plate keyboard. Ctrl/Cmd+Arrow REORDERS; a bare arrow moves the
 * cursor round the ring. NAVIGATION WRAPS - the drum is a cylinder,
 * and under a type chip the cylinder is the visible ring. Reorder
 * deliberately does not wrap (the caller refuses the ends): the order
 * is a real list with a first weapon, and that first weapon is who
 * parries. Home and End jump to the ring's ends.
 */
export function plateKeyIntent(
  e: { key: string; ctrlKey: boolean; metaKey: boolean },
  index: number,
  ring: number[],
  last: number,
): PlateKeyIntent {
  const arrow = ARROW_STEP[e.key];
  if (arrow !== undefined && (e.ctrlKey || e.metaKey)) return { kind: 'reorder', delta: arrow };
  if (arrow !== undefined) return { kind: 'goto', to: ringStep(ring, index, arrow) };
  if (e.key === 'Home') return { kind: 'goto', to: ring[0] ?? 0 };
  if (e.key === 'End') return { kind: 'goto', to: ring[ring.length - 1] ?? last };
  return null;
}
